"use client";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icons } from "@/components/Icons";
import { Button, Card, Row } from "@/components/ui";
import { useFormatPrice } from "@/hooks/useFormatPrice";
import { Price } from "@/components/Price";
import { PostOrderOffer } from "@/components/offers/PostOrderOffer";
import type { OrderPromise } from "@/api/online-ordering-service/model";
import { formatWindow, publicWindow } from "@/lib/promise";

export function SuccessScreen() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { orderId } = useParams<{ orderId: string }>();
  const state =
    (useLocation().state as {
      orderId?: string;
      displayOrderNum?: string | number;
      eta?: number;
      total?: number;
      paymentLabel?: string;
      orderType?: string | null;
      earnedPoints?: number;
      pointsToNextReward?: number;
      promise?: OrderPromise | null;
      promiseVisible?: boolean;
    } | null) || {};
  const orderIdFromState = state.orderId ?? orderId ?? "";
  // NOT `?? orderIdFromState`: on a POS-attached branch the number is empty
  // until the terminal claims the order, and falling back to the id showed
  // the customer a raw uuid where an order number belongs.
  const displayOrderNum = state.displayOrderNum ?? "";
  const eta = state.eta ?? 0;
  const total = state.total ?? 0;
  const paymentLabel = state.paymentLabel ?? "";
  const isPickup = state.orderType === "pickup";
  const { format } = useFormatPrice();
  // Promise Time: the committed window, shown only when the branch shows
  // promises (shadow mode keeps the legacy minutes row).
  const promiseWindow =
    state.promiseVisible && state.promise?.current
      ? formatWindow(publicWindow(state.promise), i18n.language)
      : null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: "0 16px",
        paddingBottom: 40,
      }}
    >
      <div style={{ height: 56 }} />
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          paddingTop: 20,
        }}
      >
        <div style={{ position: "relative", width: 120, height: 120, marginBottom: 20 }}>
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 999,
              background: "var(--primary-50)",
              transform: "scale(1.2)",
              opacity: 0.5,
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: 999,
              background: "var(--primary-600)",
              display: "grid",
              placeItems: "center",
              boxShadow: "0 18px 40px -10px rgba(11,170,87,0.5)",
            }}
          >
            <Icons.check size={56} stroke="#fff" sw={2.5} />
          </div>
        </div>
        <div className="display" style={{ fontSize: 26, fontWeight: 700, letterSpacing: -0.4 }}>{t("v2.success.order_placed")}</div>
        <div
          style={{
            fontSize: 14,
            color: "var(--ink-2)",
            marginTop: 6,
            lineHeight: 1.5,
            maxWidth: 280,
          }}
        >
          {t(isPickup ? "v2.success.sent_to_kitchen_pickup" : "v2.success.sent_to_kitchen")}
        </div>

        <Card padding={14} style={{ width: "100%", marginTop: 24, textAlign: "start" }}>
          <Row
            label={t("v2.success.order_number")}
            value={
              displayOrderNum
                ? `#${displayOrderNum}`
                : t("v2.success.order_number_pending")
            }
            bold
          />
          {promiseWindow ? (
            <Row
              label={t(isPickup ? "v2.success.estimated_ready" : "v2.success.estimated_arrival")}
              value={t("v2.success.window", { from: promiseWindow.from, to: promiseWindow.to })}
            />
          ) : (
            eta > 0 && (
              <Row
                label={t(isPickup ? "v2.success.estimated_ready" : "v2.success.estimated_arrival")}
                value={t("v2.success.min", { count: eta })}
              />
            )
          )}
          <Row label={t("v2.success.total_charged")} value={<Price amount={total} />} />
          {paymentLabel && <Row label={t("v2.success.payment")} value={paymentLabel} />}
        </Card>

        {typeof state.earnedPoints === "number" && state.earnedPoints > 0 && (
          <Card
            padding={14}
            style={{ width: "100%", marginTop: 12, display: "flex", alignItems: "center", gap: 12 }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: "var(--primary-50)",
                display: "grid",
                placeItems: "center",
                color: "var(--primary-700)",
              }}
            >
              <Icons.spark size={18} />
            </div>
            <div style={{ flex: 1, textAlign: "start" }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>
                {t("v2.success.earned_points", { count: state.earnedPoints })}
              </div>
              {typeof state.pointsToNextReward === "number" && (
                <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                  {t("v2.success.points_to_next", { count: state.pointsToNextReward })}
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Next-visit offer (Block 4 post-order surface). */}
        <PostOrderOffer />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingTop: 16 }}>
        <Button full size="lg" onClick={() => navigate(`/tracking/${orderIdFromState}`)} disabled={!orderIdFromState}>
          {t("v2.success.track_order")}
        </Button>
        <Button full size="lg" variant="ghost" onClick={() => navigate("/")}>
          {t("v2.success.back_to_menu")}
        </Button>
      </div>
    </div>
  );
}
